import { ReactElement } from 'react';

import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { Scholarship } from '@backrs_ui/common-ui/src/models/scholarship';

import { Box, Typography } from '@mui/material';

import { blueTextStyle, unorderedListStyle } from './styles';

const { ELIGIBILITY_REQUIREMENTS_STRINGS, SECTION_TITLES } = appStrings.modules.nugget.scholarship;

function generateAgeRangeString(min: number | null, max: number | null): string | undefined {
  if (min && max) {
    return `${min} - ${max}`;
  }

  if (min) {
    return `${min}+`;
  }
  if (max) {
    return `${max}-`;
  }

  return undefined;
}

export function EligibilityRequirements({
  scholarshipInfo,
}: {
  scholarshipInfo: Scholarship;
}): ReactElement {
  const levelOfStudyArray = scholarshipInfo.eligibility_level_of_study?.split(',');
  const levelOfStudyString = levelOfStudyArray
    ? levelOfStudyArray.length > 2
      ? `${levelOfStudyArray[0]} - ${levelOfStudyArray[levelOfStudyArray.length - 1]}`
      : scholarshipInfo.eligibility_level_of_study
    : '';

  const eligibilityData = [
    {
      title: '',
      value: scholarshipInfo.eligibility_time_status,
    },
    {
      title: ELIGIBILITY_REQUIREMENTS_STRINGS.LEVEL_OF_STUDY_FRONT,
      value: levelOfStudyString,
      suffix: ELIGIBILITY_REQUIREMENTS_STRINGS.LEVEL_OF_STUDY_END,
    },
    {
      title: ELIGIBILITY_REQUIREMENTS_STRINGS.INSTITUTION_TYPE,
      value: scholarshipInfo.eligibility_institution_type,
    },
    {
      title: ELIGIBILITY_REQUIREMENTS_STRINGS.LIMITED_HS,
      value: scholarshipInfo.eligibility_applicant_type_limited_hs === true ? '' : null,
    },
    {
      title: ELIGIBILITY_REQUIREMENTS_STRINGS.CITIZENSHIP,
      value: scholarshipInfo.eligibility_citizenship,
    },
    {
      title: ELIGIBILITY_REQUIREMENTS_STRINGS.AGE,
      value: generateAgeRangeString(
        scholarshipInfo.eligibility_age_min,
        scholarshipInfo.eligibility_age_max,
      ),
    },
    {
      title: ELIGIBILITY_REQUIREMENTS_STRINGS.GENDER,
      value: scholarshipInfo.eligibility_gender,
    },
    {
      title: ELIGIBILITY_REQUIREMENTS_STRINGS.RACE_ETHNICITY,
      value: scholarshipInfo.eligibility_race_ethnicity,
    },
    {
      title: ELIGIBILITY_REQUIREMENTS_STRINGS.DISABILITY_TYPE,
      value: scholarshipInfo.eligibility_disability_type,
    },
    {
      title: ELIGIBILITY_REQUIREMENTS_STRINGS.MILITARY_SERVICE,
      value: scholarshipInfo.eligibility_military_type,
    },
    {
      title: ELIGIBILITY_REQUIREMENTS_STRINGS.STATE_RESIDENCE,
      value: scholarshipInfo.prog_restrict_state_residence,
    },
    {
      title: ELIGIBILITY_REQUIREMENTS_STRINGS.COUNTY_RESIDENCE,
      value: scholarshipInfo.prog_restrict_county_residence,
    },
    {
      title: ELIGIBILITY_REQUIREMENTS_STRINGS.INSTITUTION,
      value: scholarshipInfo.prog_restrict_institution,
    },
    {
      title: ELIGIBILITY_REQUIREMENTS_STRINGS.AREA_OF_STUDY,
      value: scholarshipInfo.prog_restrict_area_study,
    },
    {
      title: ELIGIBILITY_REQUIREMENTS_STRINGS.HERITAGE,
      value: scholarshipInfo.prog_restrict_ethnicity_heritage,
    },
    {
      title: ELIGIBILITY_REQUIREMENTS_STRINGS.RELIGIOUS_AFFILIATION,
      value: scholarshipInfo.prog_restrict_religious_affiliation,
    },
  ];

  const eligibilityItems = eligibilityData
    .filter((item) => item.value !== null && item.value !== undefined && item.value !== '')
    .map((item, index) => (
      <li key={index}>
        {item.title} {item.value} {item.suffix ? item.suffix : ''}
      </li>
    ));

  return (
    <Box>
      <Typography variant="body1" sx={blueTextStyle}>
        {SECTION_TITLES.eligibilityRequirements}
      </Typography>
      <ul style={unorderedListStyle}>{eligibilityItems}</ul>
    </Box>
  );
}
